import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FaExclamationCircle } from 'react-icons/fa';

import { Button, useScreenQuery } from 'playup-web-core';

import { BodyContainer, BoldText, NormalText } from './USWithdraw';
import { WithdrawMethods } from './USWithdrawService';

const USWithdrawEnrollment = ({ selectedWithdrawMethod }: Props) => {
	const { isSmall } = useScreenQuery();
	const history = useHistory();
	const location = useLocation<{ registrationLink: string }>();

	const isVipPreferred = selectedWithdrawMethod?.withdrawal_request_type?.id === WithdrawMethods.VipPreferred.id;
	const isSkrill = selectedWithdrawMethod?.withdrawal_request_type?.id === WithdrawMethods.Skrill.id;
	const isSightline = selectedWithdrawMethod?.withdrawal_request_type?.id === WithdrawMethods.Sightline.id;

	const [registrationLink, setRegistrationLink] = React.useState('');

	React.useEffect(() => {
		if (location?.state?.registrationLink) {
			setRegistrationLink(location.state.registrationLink);
		}
	}, [location]);

	const selectedInternalWithdrawMethod = Object.values(WithdrawMethods).find((m) => {
		return m.id === selectedWithdrawMethod?.withdrawal_request_type.id;
	});

	const onEnrollClick = () => {
		if (registrationLink) {
			window.open(registrationLink, '_blank');
		}
	};

	const onCancelClick = () => {
		history.push('/');
	};

	return (
		<BodyContainer isSmallScreen={isSmall}>
			<LogoContainer>{selectedInternalWithdrawMethod?.icon}</LogoContainer>
			<NotRegisteredSection>
				<ExclamationIcon $isSmallScreen={isSmall} />
				<BoldText isSmallScreen={isSmall}>
					Looks like you haven't enrolled for a {selectedInternalWithdrawMethod?.name} account with us.
				</BoldText>
			</NotRegisteredSection>
			{isSightline && (
				<PlatformInfoSection>
					<NormalText isSmallScreen={isSmall}>
						Play+ is a fast, easy way to make deposit using a bank card, bank account, or Paypal.
					</NormalText>
					<NormalText isSmallScreen={isSmall}>
						Additional benefits include the ability to cash out to the Play+ account and access funds at ATMs or to make
						purchases everywhere Discover is accepted. Click Enroll Now below.
					</NormalText>
				</PlatformInfoSection>
			)}
			{isSkrill && (
				<PlatformInfoSection>
					<CenteredBoldText isSmallScreen={isSmall}>Hightest limits, instant payments</CenteredBoldText>
					<NormalText isSmallScreen={isSmall}>
						Enjoy instant bank payments and speedy withdrawals with the highest limits. Get sport prediction and
						advanced stats for free.
					</NormalText>
				</PlatformInfoSection>
			)}
			<SubmitButton isSmallScreen={isSmall} kind="playup" onClick={onEnrollClick} isLoading={!registrationLink}>
				Enroll Now
			</SubmitButton>
			<CancelButton isSmallScreen={isSmall} kind="hollow-black" onClick={onCancelClick}>
				Cancel
			</CancelButton>
		</BodyContainer>
	);
};

export default USWithdrawEnrollment;

interface Props {
	selectedWithdrawMethod?: WithdrawalRequestType;
}

const LogoContainer = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	width: 100%;
`;

const NotRegisteredSection = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	width: 100%;
	margin-top: 15px;
	margin-bottom: 15px;
	padding: 10px;

	border-style: solid;
	border-width: 3px;
	border-color: ${({ theme }) => theme.colors.lightGreyR};
`;

const ExclamationIcon = styled(FaExclamationCircle)<{ $isSmallScreen: boolean }>`
	height: ${({ $isSmallScreen }) => ($isSmallScreen ? '24px' : '36px')};
	width: ${({ $isSmallScreen }) => ($isSmallScreen ? '24px' : '36px')};
	color: ${({ theme }) => theme.colors.darkR};
	margin-right: ${({ $isSmallScreen }) => ($isSmallScreen ? '8px' : '12px')};
`;

const PlatformInfoSection = styled.section`
	margin-top: 15px;
	margin-bottom: 15px;

	p {
		margin-bottom: 10px;
	}
`;

const CenteredBoldText = styled.p<{ isSmallScreen: boolean }>`
	margin: 0;
	fontsize: ${({ isSmallScreen }) => (isSmallScreen ? '14px' : '18px')};
	font-weight: 600;
	color: ${({ theme }) => theme.colors.darkR};
	text-align: center;
`;

const SubmitButton = styled(Button)<{ isSmallScreen: boolean }>`
	margin-top: ${({ isSmallScreen }) => (isSmallScreen ? '30px' : '40px')};
	margin-bottom: 10px;
`;

const CancelButton = styled(Button)<{ isSmallScreen: boolean }>`
	margin-bottom: ${({ isSmallScreen }) => (isSmallScreen ? '10px' : '20px')};
`;
